import { createAsyncThunk,createSlice } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';

import api from '../../api';

export const getAllUsers = createAsyncThunk('users/getAllUsers', async (_, { rejectWithValue }) => {
  try {
    const res = await api.get(`/users`);

    return res.data.data;
  } catch (error) {
    toast.warn(error.errors.message);
    const errorMessage =
      error?.error ||
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      error?.errors?.[0]?.message ||
      'Something went wrong trying to fetch all users.';
    return rejectWithValue(errorMessage);
  }
});

export const deleteUser = createAsyncThunk(
  'users/deleteUser',
  async (userID, { rejectWithValue }) => {
    try {
      await api.delete(`/users/${userID}`);
      return userID;
    } catch (error) {
      toast.warn(error.errors.message);
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to delete that user.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const getSpecificUser = createAsyncThunk(
  'users/getSpecificUser',
  async (userID, { rejectWithValue }) => {
    try {
      const res = await api.get(`/users/${userID}`);

      return res.data.data.user;
    } catch (error) {
      toast.warn(error.errors.message);
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        "Something went wrong trying to access that user's information.";
      return rejectWithValue(errorMessage);
    }
  }
);

export const updateUser = createAsyncThunk(
  'users/updateUser',
  async (userInfo, { rejectWithValue }) => {
    const {
      userId,
      firstName,
      lastName,
      email,
      phoneNumber,
      address,
      password,
      practices,
      farmLocations,
      role,
    } = userInfo;
    try {
      const response = await api.put(`/users/${userId}`, {
        firstName,
        lastName,
        email,
        phoneNumber,
        address,
        password,
        practices,
        farmLocations,
        role,
      });

      return response.data.data.user;
    } catch (error) {
      const errorMessage =
        error?.error ||
        error.response?.data?.error ||
        error.response?.data?.message ||
        error.message ||
        error?.errors?.[0]?.message ||
        'Something went wrong trying to change user info.';
      toast.warn(errorMessage);
      return rejectWithValue(errorMessage);
    }
  }
);

/**Register new account*/
export const register = createAsyncThunk('auth/register', async (formData, { rejectWithValue }) => {
  try {
    const res = await api.post('/users', formData);
    toast.success(`Successfully Registered!, ${formData.email}!`);
    return res.data.data.user;
  } catch (error) {
    const errorMessage =
      error?.error ||
      error.response?.data?.error ||
      error.response?.data?.message ||
      error.message ||
      error?.errors?.[0]?.message ||
      'Something went wrong trying to sign up the user.';
    return rejectWithValue(errorMessage);
  }
});

const initialState = {
  user: null,
  users: [],
  loading: false,
  error: null,
  signUpSuccess: false,
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllUsers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        state.users = action.payload.users;
        state.loading = false;
      })
      .addCase(getAllUsers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUser.fulfilled, (state, action) => {
        state.loading = false;
        state.users = state.users.filter((user) => user._id !== action.payload);
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(deleteUser.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
        const updatedUser = action.payload;

        const userIndex = state.users.findIndex((user) => user._id === updatedUser._id);
        if (userIndex !== -1) {
          state.users[userIndex] = updatedUser;
        }

        if (state.user && state.user._id === updatedUser._id) {
          state.user = updatedUser;
        }
      })
      .addCase(updateUser.pending, (state, action) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.pending, (state) => {
        state.loading = true;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.signUpSuccess = true;
        state.users = [...state.users, action.payload];
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSpecificUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(getSpecificUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(getSpecificUser.pending, (state) => {
        state.loading = true;
      });
  },
});

export const { clearError } = userSlice.actions;
export default userSlice.reducer;
